/* hs-eslint ignored failing-rules */
/* eslint-disable react/prop-types */

'use es6';

import PropTypes from 'prop-types';
import { useState, useEffect, useRef, useCallback, memo, useLayoutEffect } from 'react';
import tinymce from 'tinymce';
import { isMceUI } from 'tinymce-themes/utils/dom';
import { EVENTS } from '../constants';
import usePrevious from '../hooks/usePrevious';
import useTrellisTheme from '../hooks/useTrellisTheme';
import { shouldReinitialize, controlledContentChanged, hasKeyChanged, setupMouseEvents, monitorUtils } from '../utils';
import TinymceIframe from './TinymceIframe';
import TinymceInline from './TinymceInline';
import { jsx as _jsx } from "react/jsx-runtime";
const Modes = {
  READONLY: 'readonly',
  DESIGN: 'design'
};
const InitializationStatuses = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  FINISHED: 'finished'
};
let nextId = 1;
const getId = () => {
  const id = `react-tinymce-${nextId}`;
  nextId++;
  return id;
};
let initCount = 1;
const generateInitStatus = () => {
  const initStatus = `${InitializationStatuses.NOT_STARTED}_${initCount}`;
  initCount++;
  return initStatus;
};
const withEditorRef = WrappedComponent => {
  return props => {
    const editorRef = useRef(null);
    return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({
      editorRef: editorRef
    }, props));
  };
};
const getEvents = props => {
  const eventKeys = Object.keys(EVENTS);
  const events = eventKeys.map(mceEventName => {
    return {
      mceEventName,
      handler: props[EVENTS[mceEventName]]
    };
  });
  return events;
};
const isEqual = (oldProps, newProps) => {
  const editor = newProps.editorRef.current;
  const editorFocus = editor ? editor.hasFocus() || isMceUI(document.activeElement) : false;
  if (editorFocus && !newProps.readOnly && !hasKeyChanged(oldProps, newProps)) {
    return true;
  }
  return !shouldReinitialize(oldProps, newProps) && !controlledContentChanged(oldProps, newProps, editor) && oldProps.readOnly === newProps.readOnly;
};
const TinyMCE = /*#__PURE__*/memo(props => {
  const {
    config,
    uniqueKey,
    content,
    readOnly,
    target,
    className = '',
    connectDropTarget,
    tinyMceRef,
    editorRef,
    placeholder,
    minHeight,
    maxHeight,
    'data-onboarding': dataOnboarding,
    'data-test-id': dataTestId
  } = props;
  const [initializationStatus, setInitializationStatus] = useState(() => generateInitStatus());
  const lastInitializationStatus = useRef(initializationStatus);
  const id = useRef(getId());
  const contentRef = useRef(content);
  const prevConfig = usePrevious(config);
  const prevUniqueKey = usePrevious(uniqueKey);
  const prevContent = usePrevious(content);

  // Setup trellis theme for usage within tinymce-plugins
  useTrellisTheme();

  // This means that the initialize callback will be created on every render,
  // which is not ideal. However I'm not sure there is a good way to
  // listen to only the dynamic event props. The callback still is needed
  // because of the use of setInitializationStatus
  const events = getEvents(props);
  const hasToolbar = !!(editorRef.current && editorRef.current.getParam('canvasToolbar'));
  const initialize = useCallback(() => {
    const selector = target ? undefined : `#${id.current}`;
    const setupCallback = config.setup;
    contentRef.current = content;
    monitorUtils.reportInitializations(id.current, config.hsPlugins && config.hsPlugins.appName);
    if (lastInitializationStatus.current !== InitializationStatuses.IN_PROGRESS) {
      // Prevent race condition where subsequent call to `initialize` was
      // overriding the async `setInitializationStatus(FINISHED)` call in
      // editor.on('init')
      setInitializationStatus(InitializationStatuses.IN_PROGRESS);
    }
    const setup = _editor => {
      editorRef.current = _editor;
      tinyMceRef({
        editor: _editor
      });
      setupMouseEvents(_editor);
      _editor.on('init', () => {
        if (contentRef.current != null) {
          _editor.setContent(contentRef.current);
        }
        if (readOnly) {
          _editor.setMode(Modes.READONLY);
        }
        setInitializationStatus(InitializationStatuses.FINISHED);
      });
      events.forEach(({
        mceEventName,
        handler
      }) => {
        if (typeof handler !== 'function') {
          return;
        }
        _editor.on(mceEventName, event => {
          // native DOM events don't have access to the editor so we pass it here
          handler(event, _editor);
        });
      });
      if (typeof setupCallback === 'function') {
        setupCallback(_editor);
      }
    };
    tinymce.init(Object.assign({}, config, {
      placeholder,
      selector,
      target,
      setup,
      custom_ui_selector: '[data-tinymce-floating-element]' // Treat trellis floating elements as "editor ui" elements to prevent blurring (used to rely on mce-* classnames)
    }));
  }, [config, readOnly, target, tinyMceRef, editorRef, events, content, placeholder]);
  const remove = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.remove();
    }
    setInitializationStatus(generateInitStatus());
  }, [editorRef]);
  useEffect(() => {
    lastInitializationStatus.current = initializationStatus;
    if (initializationStatus !== InitializationStatuses.FINISHED) {
      initialize();
    }
  }, [initialize, initializationStatus]);

  // Did update logic for read only
  useEffect(() => {
    if (editorRef.current && initializationStatus === InitializationStatuses.FINISHED) {
      editorRef.current.setMode(readOnly ? Modes.READONLY : Modes.DESIGN);
    }
  }, [editorRef, initializationStatus, readOnly]);

  // Did update logic for reinit
  useEffect(() => {
    const oldProps = {
      config: prevConfig,
      content: prevContent,
      uniqueKey: prevUniqueKey
    };
    const newProps = {
      config,
      content,
      uniqueKey
    };
    if (editorRef.current && shouldReinitialize(oldProps, newProps)) {
      console.warn(`[Reinitializing TinyMCE] Editor with id '${id.current}' was forced to reinitialize.\nIf this was not intended, please check that your 'config' is properly memoized or if you should be including a 'uniqueKey'.`);
      remove();
    } else if (controlledContentChanged(oldProps, newProps, editorRef.current)) {
      editorRef.current.setContent(content);
      contentRef.current = content;
    }
  }, [prevConfig, config, prevUniqueKey, uniqueKey, prevContent, content, remove, editorRef]);
  useLayoutEffect(() => {
    return () => {
      remove();
    };
  }, [remove]);
  const initialized = initializationStatus === InitializationStatuses.FINISHED;
  return config.inline ?
  /*#__PURE__*/
  // @bend-expect trellis-tokenized
  _jsx(TinymceInline, {
    className: className,
    minHeight: minHeight,
    maxHeight: maxHeight,
    editor: editorRef.current,
    config: config,
    content: contentRef.current,
    uniqueKey: uniqueKey,
    target: target,
    connectDropTarget: connectDropTarget,
    initialized: initialized,
    id: id.current,
    "data-onboarding": dataOnboarding,
    "data-test-id": dataTestId
  }) :
  /*#__PURE__*/
  // @bend-expect trellis-tokenized
  _jsx(TinymceIframe, {
    hasToolbar: hasToolbar,
    readOnly: readOnly,
    content: contentRef.current,
    initialized: initialized,
    editor: editorRef.current,
    className: className,
    id: id.current,
    "data-onboarding": dataOnboarding,
    "data-test-id": dataTestId
  });
}, isEqual);
const propTypes = {
  config: PropTypes.object,
  content: PropTypes.string,
  readOnly: PropTypes.bool,
  className: PropTypes.string,
  uniqueKey: PropTypes.string,
  target: PropTypes.instanceOf(Element),
  connectDropTarget: PropTypes.func,
  tinyMceRef: PropTypes.func,
  editorRef: PropTypes.object,
  placeholder: PropTypes.string,
  'data-onboarding': PropTypes.string,
  'data-test-id': PropTypes.string
};

// add handler propTypes
Object.keys(EVENTS).forEach(name => {
  propTypes[EVENTS[name]] = PropTypes.func;
});
TinyMCE.propTypes = propTypes;

/* eslint-disable-next-line hubspot-dev/no-default-props-assignment */
TinyMCE.defaultProps = {
  readOnly: false,
  uniqueKey: null
};
export default withEditorRef(TinyMCE);